import React, { useMemo, useState } from 'react';
import { colors } from '../theme/tokens';

const STATUS_COLORS = {
  stable: '#2fb65c',
  unstable: '#f4d03f',
  competitor: '#e74c3c',
  lead: '#ff69b4',
  technical: '#9b59b6'
};

const STATUS_LABELS = {
  stable: 'Stable',
  unstable: 'Unstable',
  competitor: 'Competitor',
  lead: 'Lead',
  technical: 'Technical'
};

const getTimeAgo = (date) => {
  if (!date) return 'Never';
  const diffDays = Math.floor((new Date() - new Date(date)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const TeamAnalytics = ({ markers, salesTeam }) => {
  const [sortField, setSortField] = useState('claimed');
  const [sortDir, setSortDir] = useState('desc');

  const stats = useMemo(() => {
    return salesTeam.map(member => {
      const memberId = member._id?.toString();

      // Clients claimed by this member
      const claimed = markers.filter(m => m.claimedBy?.toString() === memberId);

      // Status breakdown of claimed clients
      const statusCounts = Object.keys(STATUS_LABELS).reduce((acc, s) => {
        acc[s] = claimed.filter(m => m.status === s).length;
        return acc;
      }, {});

      // Total messages sent by this member across all markers
      const messageCount = markers.reduce((sum, marker) => {
        return sum + (marker.messages || []).filter(msg => msg.userId?.toString() === memberId).length;
      }, 0);

      // Clients created by this member
      const created = markers.filter(m => m.createdBy?.toString() === memberId).length;

      // Last activity: most recent message sent
      let lastActivity = null;
      markers.forEach(marker => {
        (marker.messages || []).forEach(msg => {
          if (msg.userId?.toString() === memberId) {
            if (!lastActivity || new Date(msg.createdAt) > new Date(lastActivity)) {
              lastActivity = msg.createdAt;
            }
          }
        });
      });

      // Buying from competitor count (most at-risk metric)
      const atRisk = statusCounts.competitor + statusCounts.unstable;

      return {
        ...member,
        claimedCount: claimed.length,
        statusCounts,
        messageCount,
        created,
        lastActivity,
        atRisk
      };
    });
  }, [markers, salesTeam]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕';

  const sorted = [...stats].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'name') cmp = a.name.localeCompare(b.name);
    else if (sortField === 'claimed') cmp = a.claimedCount - b.claimedCount;
    else if (sortField === 'messages') cmp = a.messageCount - b.messageCount;
    else if (sortField === 'created') cmp = a.created - b.created;
    else if (sortField === 'atRisk') cmp = a.atRisk - b.atRisk;
    else if (sortField === 'lastActivity') cmp = new Date(a.lastActivity || 0) - new Date(b.lastActivity || 0);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Team-level totals
  const totals = useMemo(() => ({
    claimed: stats.reduce((s, m) => s + m.claimedCount, 0),
    messages: stats.reduce((s, m) => s + m.messageCount, 0),
    created: stats.reduce((s, m) => s + m.created, 0),
    atRisk: stats.reduce((s, m) => s + m.atRisk, 0),
  }), [stats]);

  // Overall status distribution across all markers
  const overallStatus = useMemo(() => {
    return Object.keys(STATUS_LABELS).reduce((acc, s) => {
      acc[s] = markers.filter(m => m.status === s).length;
      return acc;
    }, {});
  }, [markers]);

  const SH = ({ field, children }) => (
    <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(field)}>
      {children}{sortIcon(field)}
    </th>
  );

  if (salesTeam.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>📊</div>
        <p>No sales team data available</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>

      {/* ── Summary cards ── */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryNum}>{markers.length}</div>
          <div style={styles.summaryLabel}>Total Clients</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryNum, color: '#ff69b4' }}>
            {markers.filter(m => m.status === 'lead' && !m.claimedBy).length}
          </div>
          <div style={styles.summaryLabel}>Unclaimed Leads</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryNum, color: '#e74c3c' }}>{totals.atRisk}</div>
          <div style={styles.summaryLabel}>At-Risk Clients</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryNum, color: '#2fb65c' }}>{totals.messages}</div>
          <div style={styles.summaryLabel}>Total Messages</div>
        </div>
      </div>

      {/* ── Overall status distribution ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>📊 Portfolio Status Breakdown</h3>
        <div style={styles.statusDistRow}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <div key={key} style={styles.statusDistCard}>
              <div style={{ ...styles.statusDistNum, color: STATUS_COLORS[key] }}>
                {overallStatus[key]}
              </div>
              <div style={{ ...styles.statusDistBadge, background: STATUS_COLORS[key] }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-member table ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>👥 Team Performance</h3>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <SH field="name">Team Member</SH>
                <SH field="claimed">Claimed</SH>
                <th style={styles.th}>Status Mix</th>
                <SH field="atRisk">At Risk</SH>
                <SH field="messages">Messages</SH>
                <SH field="created">Created</SH>
                <SH field="lastActivity">Last Active</SH>
              </tr>
            </thead>
            <tbody>
              {sorted.map(member => (
                <tr key={member._id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.memberCell}>
                      {member.picture ? (
                        <img src={member.picture} alt={member.name} style={styles.avatar} />
                      ) : (
                        <div style={styles.avatarPlaceholder}>
                          {(member.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={styles.memberName}>{member.name}</div>
                        <div style={{ ...styles.roleBadge, background: member.salesRole === 'manager' ? colors.surface2 : '#91C83E', color: member.salesRole === 'manager' ? colors.text : '#10140f' }}>
                          {member.salesRole === 'manager' ? '👔 Manager' : '📊 Rep'}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td style={styles.td}>
                    <span style={styles.numBadge}>{member.claimedCount}</span>
                  </td>

                  <td style={styles.td}>
                    <div style={styles.statusMix}>
                      {Object.entries(member.statusCounts).map(([status, count]) =>
                        count > 0 ? (
                          <span
                            key={status}
                            title={`${STATUS_LABELS[status]}: ${count}`}
                            style={{ ...styles.statusPip, background: STATUS_COLORS[status] }}
                          >
                            {count}
                          </span>
                        ) : null
                      )}
                      {member.claimedCount === 0 && <span style={styles.noData}>—</span>}
                    </div>
                  </td>

                  <td style={styles.td}>
                    <span style={{
                      ...styles.numBadge,
                      background: member.atRisk > 0 ? 'rgba(231,76,60,0.18)' : colors.surface2,
                      color: member.atRisk > 0 ? '#ff8178' : colors.textMuted
                    }}>
                      {member.atRisk}
                    </span>
                  </td>

                  <td style={styles.td}>
                    <span style={{ ...styles.numBadge, background: 'rgba(47,182,92,0.18)', color: colors.parGreenBright }}>
                      {member.messageCount}
                    </span>
                  </td>

                  <td style={styles.td}>
                    <span style={styles.numBadge}>{member.created}</span>
                  </td>

                  <td style={{ ...styles.td, color: !member.lastActivity ? colors.textFaint : colors.textMuted, fontSize: '12px' }}>
                    {getTimeAgo(member.lastActivity)}
                  </td>
                </tr>
              ))}
            </tbody>

            {/* Totals row */}
            <tfoot>
              <tr style={{ background: colors.surfaceSoft, borderTop: `2px solid ${colors.border}` }}>
                <td style={{ ...styles.td, fontWeight: '700', color: colors.text }}>
                  Team Total ({salesTeam.length} members)
                </td>
                <td style={styles.td}><span style={styles.numBadge}>{totals.claimed}</span></td>
                <td style={styles.td} />
                <td style={styles.td}>
                  <span style={{ ...styles.numBadge, background: totals.atRisk > 0 ? 'rgba(231,76,60,0.18)' : colors.surface2, color: totals.atRisk > 0 ? '#ff8178' : colors.textMuted }}>
                    {totals.atRisk}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.numBadge, background: 'rgba(47,182,92,0.18)', color: colors.parGreenBright }}>
                    {totals.messages}
                  </span>
                </td>
                <td style={styles.td}><span style={styles.numBadge}>{totals.created}</span></td>
                <td style={styles.td} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Per-member detail cards (status breakdown) ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🗂️ Individual Status Breakdown</h3>
        <div style={styles.memberCards}>
          {sorted.map(member => (
            <div key={member._id} style={styles.memberCard}>
              <div style={styles.memberCardHeader}>
                {member.picture ? (
                  <img src={member.picture} alt={member.name} style={styles.cardAvatar} />
                ) : (
                  <div style={{ ...styles.cardAvatar, ...styles.avatarPlaceholder, width: '36px', height: '36px', fontSize: '16px', lineHeight: '36px' }}>
                    {(member.name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={styles.cardMemberName}>{member.name}</div>
                  <div style={{ ...styles.roleBadge, background: member.salesRole === 'manager' ? colors.surface2 : '#91C83E', color: member.salesRole === 'manager' ? colors.text : '#10140f', display: 'inline-block' }}>
                    {member.salesRole === 'manager' ? '👔 Manager' : '📊 Rep'}
                  </div>
                </div>
              </div>

              <div style={styles.cardStats}>
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <div key={key} style={styles.cardStatRow}>
                    <div style={styles.cardStatDot}>
                      <span style={{ ...styles.dot, background: STATUS_COLORS[key] }} />
                      <span style={styles.cardStatLabel}>{label}</span>
                    </div>
                    <span style={{
                      ...styles.cardStatNum,
                      fontWeight: member.statusCounts[key] > 0 ? '700' : '400',
                      color: member.statusCounts[key] > 0 ? STATUS_COLORS[key] : colors.textFaint
                    }}>
                      {member.statusCounts[key]}
                    </span>
                  </div>
                ))}
                <div style={{ ...styles.cardStatRow, borderTop: `1px solid ${colors.glassBorder}`, marginTop: '6px', paddingTop: '6px' }}>
                  <span style={{ fontSize: '12px', color: colors.textMuted }}>💬 Messages sent</span>
                  <span style={{ ...styles.cardStatNum, color: colors.parGreenBright, fontWeight: '700' }}>{member.messageCount}</span>
                </div>
                <div style={styles.cardStatRow}>
                  <span style={{ fontSize: '12px', color: colors.textMuted }}>🕒 Last active</span>
                  <span style={{ fontSize: '12px', color: colors.textMuted }}>{getTimeAgo(member.lastActivity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '20px' },
  empty: { textAlign: 'center', padding: '60px', color: colors.textMuted },
  summaryRow: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  summaryCard: {
    flex: '1 1 120px', background: colors.glass, padding: '18px 20px',
    borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    textAlign: 'center', border: `1px solid ${colors.glassBorder}`
  },
  summaryNum: { fontSize: '32px', fontWeight: '800', color: colors.text, lineHeight: 1 },
  summaryLabel: { fontSize: '12px', color: colors.textMuted, marginTop: '6px', fontWeight: '500' },
  section: { marginBottom: '28px' },
  sectionTitle: { margin: '0 0 14px 0', fontSize: '16px', fontWeight: '700', color: colors.text },
  statusDistRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  statusDistCard: {
    flex: '1 1 100px', background: colors.glass, padding: '14px 16px',
    borderRadius: '8px', textAlign: 'center', border: `1px solid ${colors.glassBorder}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
  },
  statusDistNum: { fontSize: '28px', fontWeight: '800', lineHeight: 1 },
  statusDistBadge: {
    display: 'inline-block', color: 'white', padding: '3px 8px',
    borderRadius: '8px', fontSize: '11px', fontWeight: '600', marginTop: '6px'
  },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', border: `1px solid ${colors.glassBorder}` },
  table: { width: '100%', borderCollapse: 'collapse', background: colors.glass },
  th: { textAlign: 'left', padding: '12px 14px', background: colors.surfaceSoft, fontWeight: '600', fontSize: '13px', color: colors.text, borderBottom: `2px solid ${colors.border}`, whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: '12px 14px', fontSize: '13px', color: colors.text, verticalAlign: 'middle' },
  memberCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', border: `2px solid ${colors.border}`, flexShrink: 0 },
  avatarPlaceholder: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #2fb65c, #1d8c3a)',
    color: 'white', fontSize: '14px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  memberName: { fontWeight: '600', fontSize: '13px', color: colors.text },
  roleBadge: { display: 'inline-block', color: 'white', padding: '1px 7px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', marginTop: '2px' },
  numBadge: { display: 'inline-block', background: colors.surface2, color: colors.text, padding: '3px 10px', borderRadius: '10px', fontSize: '13px', fontWeight: '700' },
  statusMix: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  statusPip: { color: 'white', padding: '2px 7px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'default' },
  noData: { color: colors.textFaint, fontSize: '12px' },
  memberCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' },
  memberCard: { background: colors.glass, borderRadius: '10px', padding: '16px', border: `1px solid ${colors.glassBorder}`, boxShadow: '0 2px 6px rgba(0,0,0,0.25)' },
  memberCardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', paddingBottom: '12px', borderBottom: `1px solid ${colors.glassBorder}` },
  cardAvatar: { width: '36px', height: '36px', borderRadius: '50%', border: `2px solid ${colors.border}`, flexShrink: 0 },
  cardMemberName: { fontWeight: '700', fontSize: '14px', color: colors.text },
  cardStats: { display: 'flex', flexDirection: 'column', gap: '6px' },
  cardStatRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardStatDot: { display: 'flex', alignItems: 'center', gap: '6px' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  cardStatLabel: { fontSize: '12px', color: colors.textMuted },
  cardStatNum: { fontSize: '13px' }
};

export default TeamAnalytics;
